import React, { useEffect, useState, useRef } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import {
  LogOut, Store, Zap, BarChart3, Loader2, Save, Info, ExternalLink,
  RotateCcw, Upload, Star, X, GripVertical, Instagram, Facebook,
  FileText, Sparkles, Crown, ChevronDown, ChevronUp, CheckCircle2,
  AlertCircle, Tag, CalendarX
} from 'lucide-react';
import { sponsorApi, isSponsorAuthed, sponsorLogout, resolveImage, API } from '../../api';

const TABS = [
  { key: 'activity', label: 'La mia attività', icon: Store },
  { key: 'offers', label: 'Offerte flash', icon: Zap },
  { key: 'upgrades', label: 'Sponsorizzazione', icon: Crown },
  { key: 'stats', label: 'Statistiche', icon: BarChart3 },
];

// Tier labels & prices (allineati a backend SPONSOR_TIER_PRICES)
const TIER_INFO = {
  none: { label: 'Base', price: 0, color: 'text-white/60', desc: 'Scheda base gratuita' },
  featured: { label: 'In evidenza', price: 29, color: 'text-blue-400', desc: 'Badge oro + boost' },
  top3: { label: 'Top 3', price: 79, color: 'text-amber-400', desc: 'Prime 3 posizioni della categoria' },
  recommended: { label: 'Consigliato da YUP', price: 149, color: 'text-[color:var(--yup-primary)]', desc: 'Nastro premium + massima visibilità' },
};
const TIER_HIERARCHY = ['none', 'featured', 'top3', 'recommended'];

// Available amenity tags (multiselect)
const AVAILABLE_TAGS = [
  { key: 'family-friendly', label: 'Adatto ai bambini' },
  { key: 'pet-friendly', label: 'Pet friendly' },
  { key: 'terrace', label: 'Terrazza / Vista lago' },
  { key: 'reservation-required', label: 'Prenotazione consigliata' },
  { key: 'wheelchair-accessible', label: 'Accessibile in sedia a rotelle' },
  { key: 'wifi', label: 'Wi-Fi gratuito' },
  { key: 'parking', label: 'Parcheggio disponibile' },
  { key: 'outdoor', label: 'Spazio all\'aperto' },
  { key: 'vegan-options', label: 'Opzioni vegane' },
  { key: 'gluten-free', label: 'Senza glutine' },
];

export default function SponsorDashboard() {
  const nav = useNavigate();
  const [me, setMe] = useState(null);
  const [tab, setTab] = useState('activity');
  const [loading, setLoading] = useState(true);
  const [activities, setActivities] = useState([]);
  const [activeActId, setActiveActId] = useState(null);
  const [offers, setOffers] = useState([]);
  const [stats, setStats] = useState(null);
  const [upgrades, setUpgrades] = useState([]);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [msgType, setMsgType] = useState('ok'); // ok | err
  const [resetting, setResetting] = useState(false);

  const loadAll = async () => {
    const [m, a, o, s, u] = await Promise.all([
      sponsorApi.me(), sponsorApi.myActivities(), sponsorApi.myOffers(),
      sponsorApi.analytics(30), sponsorApi.listUpgrades()
    ]);
    setMe(m); setActivities(a); setOffers(o); setStats(s); setUpgrades(u);
    if (a.length && !activeActId) setActiveActId(a[0].id);
  };

  useEffect(() => {
    if (!isSponsorAuthed()) { nav('/sponsor/login'); return; }
    loadAll().catch(console.error).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nav]);

  if (!isSponsorAuthed()) return <Navigate to="/sponsor/login" replace />;
  if (loading) return <div className="min-h-screen flex items-center justify-center bg-black text-white/70"><Loader2 className="animate-spin" size={32}/></div>;

  const logout = () => { sponsorLogout(); nav('/sponsor/login'); };

  const notify = (t, type = 'ok') => { setMsg(t); setMsgType(type); setTimeout(() => setMsg(''), 3500); };

  const updateActivity = async (id, patch) => {
    setSaving(true); setMsg('');
    try {
      await sponsorApi.updateActivity(id, patch);
      const a = await sponsorApi.myActivities();
      setActivities(a);
      notify('Modifiche salvate ✓', 'ok');
    } catch (e) { notify('Errore: ' + (e.response?.data?.detail || e.message), 'err'); }
    finally { setSaving(false); }
  };

  const doDemoReset = async () => {
    if (!window.confirm('Ripristinare i dati demo allo stato iniziale?')) return;
    setResetting(true);
    try {
      await sponsorApi.demoReset();
      await loadAll();
      notify('Dati demo ripristinati ✓', 'ok');
    } catch (e) { notify('Errore reset: ' + (e.response?.data?.detail || e.message), 'err'); }
    finally { setResetting(false); }
  };

  const reloadOffers = async () => setOffers(await sponsorApi.myOffers());
  const reloadUpgrades = async () => setUpgrades(await sponsorApi.listUpgrades());

  const activeActivity = activities.find(a => a.id === activeActId) || activities[0];

  return (
    <div className="min-h-screen text-white" style={{ background: 'linear-gradient(180deg, #0b0b0b, #000)' }}>
      {/* Demo banner with reset button */}
      {me?.is_demo && (
        <div className="bg-gradient-to-r from-amber-500 to-yellow-600 text-black font-semibold py-2 px-4 text-sm sticky top-0 z-40 flex items-center justify-center gap-4 flex-wrap" data-testid="sponsor-demo-banner">
          <span>🎬 Modalità dimostrativa — i dati si resettano ogni 24h. Provalo pure!</span>
          <button
            onClick={doDemoReset}
            disabled={resetting}
            data-testid="sponsor-demo-reset-btn"
            className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-black/25 hover:bg-black/40 text-xs font-bold uppercase tracking-wider disabled:opacity-50"
          >
            {resetting ? <Loader2 size={12} className="animate-spin"/> : <RotateCcw size={12}/>}
            Ripristina ora
          </button>
        </div>
      )}

      <header className="border-b border-white/10 bg-black/70 backdrop-blur-md sticky z-30" style={{ top: me?.is_demo ? '36px' : '0' }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest text-[color:var(--yup-primary)] font-bold">Portale Sponsor YUP</div>
            <div className="text-white font-semibold text-base sm:text-lg truncate">{me?.display_name || me?.email}</div>
          </div>
          <button onClick={logout} className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-sm" data-testid="sponsor-logout-btn">
            <LogOut size={14}/> <span className="hidden sm:inline">Esci</span>
          </button>
        </div>
        <nav className="max-w-6xl mx-auto px-4 sm:px-6 flex gap-1 overflow-x-auto scrollbar-hide">
          {TABS.map(t => {
            const Icon = t.icon;
            const active = tab === t.key;
            return (
              <button key={t.key} onClick={() => setTab(t.key)}
                      data-testid={`sponsor-tab-${t.key}`}
                      className={`inline-flex items-center gap-2 px-4 py-3 text-sm font-semibold border-b-2 whitespace-nowrap transition ${
                        active ? 'border-[color:var(--yup-primary)] text-[color:var(--yup-primary)]' : 'border-transparent text-white/60 hover:text-white'
                      }`}>
                <Icon size={16}/> {t.label}
              </button>
            );
          })}
        </nav>
      </header>

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-6">
        {msg && (
          <div
            className={`mb-4 px-3 py-2 rounded text-sm inline-flex items-center gap-2 ${msgType === 'ok' ? 'bg-emerald-500/20 text-emerald-300' : 'bg-red-500/20 text-red-300'}`}
            data-testid="sponsor-save-msg"
          >
            {msgType === 'ok' ? <CheckCircle2 size={14}/> : <AlertCircle size={14}/>} {msg}
          </div>
        )}

        {/* Multi-activity selector */}
        {activities.length > 1 && ['activity', 'upgrades'].includes(tab) && (
          <div className="mb-6 flex flex-wrap gap-2" data-testid="sponsor-activity-selector">
            <span className="text-xs uppercase tracking-widest text-white/50 font-semibold self-center mr-2">Attività:</span>
            {activities.map(a => (
              <button
                key={a.id}
                onClick={() => setActiveActId(a.id)}
                data-testid={`sponsor-select-activity-${a.id}`}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition ${
                  a.id === activeActId
                    ? 'bg-[color:var(--yup-primary)] border-[color:var(--yup-primary)] text-black'
                    : 'border-white/15 text-white/70 hover:border-white/30'
                }`}
              >
                {a.name}
              </button>
            ))}
          </div>
        )}

        {tab === 'activity' && (
          <div className="space-y-6">
            {activities.length === 0 && (
              <div className="text-center py-16 text-white/60">
                <Store size={48} className="mx-auto mb-4 opacity-30"/>
                <p>Nessuna attività collegata al tuo account. Contatta lo staff YUP.</p>
              </div>
            )}
            {activeActivity && (
              <ActivityEditor
                key={activeActivity.id}
                activity={activeActivity}
                saving={saving}
                onSave={patch => updateActivity(activeActivity.id, patch)}
              />
            )}
          </div>
        )}

        {tab === 'offers' && (
          <OffersSection activities={activities} offers={offers} onChange={reloadOffers} />
        )}

        {tab === 'upgrades' && activeActivity && (
          <UpgradesSection
            activity={activeActivity}
            upgrades={upgrades.filter(u => u.activity_id === activeActivity.id)}
            onChange={reloadUpgrades}
            notify={notify}
          />
        )}

        {tab === 'stats' && (
          <StatsSection stats={stats} />
        )}
      </main>
    </div>
  );
}

// ============ ACTIVITY EDITOR ============

function extractDescription(desc) {
  if (!desc) return '';
  if (typeof desc === 'string') return desc;
  return desc.it || desc.en || Object.values(desc)[0] || '';
}

function ActivityEditor({ activity, saving, onSave }) {
  const [f, setF] = useState(() => ({
    name: activity.name || '',
    description: extractDescription(activity.description),
    address: activity.address || '',
    phone: activity.phone || '',
    website: activity.website || '',
    email: activity.email || '',
    hours: activity.hours || {},
    image: activity.image || '',
    gallery: activity.gallery || [],
    instagram_url: activity.instagram_url || '',
    facebook_url: activity.facebook_url || '',
    menu_url: activity.menu_url || '',
    tags: activity.tags || [],
    extraordinary_closures: activity.extraordinary_closures || [],
    featured_offer_text: activity.featured_offer_text || '',
  }));
  const [closureDate, setClosureDate] = useState('');

  const handleSave = () => {
    onSave({
      ...f,
      description: f.description ? { ...(activity.description || {}), it: f.description } : {},
    });
  };

  return (
    <div className="rounded-2xl bg-white/[0.03] border border-white/10 p-4 sm:p-6" data-testid={`sponsor-activity-${activity.id}`}>
      <div className="flex items-start gap-4 mb-6">
        {(f.image || f.gallery?.[0]) && (
          <img src={resolveImage(f.image || f.gallery[0])} alt="" className="w-20 h-20 sm:w-24 sm:h-24 rounded-lg object-cover flex-shrink-0"/>
        )}
        <div className="min-w-0">
          <h2 className="text-xl font-bold text-white truncate">{activity.name}</h2>
          <div className="text-xs text-white/50 mt-1">{activity.city_slug} · {activity.category_key}</div>
          <div className="mt-1 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full bg-white/5 border border-white/10">
            <Crown size={10}/> Livello: <span className={TIER_INFO[activity.sponsor_level || 'none'].color}>{TIER_INFO[activity.sponsor_level || 'none'].label}</span>
          </div>
        </div>
      </div>

      {/* Photo gallery — drag & drop upload with reorder */}
      <SectionTitle icon={Upload}>Foto & Galleria</SectionTitle>
      <GalleryEditor
        image={f.image}
        gallery={f.gallery}
        onChange={({ image, gallery }) => setF({ ...f, image, gallery })}
      />

      {/* Basic info */}
      <SectionTitle icon={Store}>Informazioni principali</SectionTitle>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label="Nome"><Input value={f.name} onChange={v => setF({...f, name: v})} testid="field-name"/></Field>
        <Field label="Telefono"><Input value={f.phone} onChange={v => setF({...f, phone: v})} placeholder="+39 ..." testid="field-phone"/></Field>
        <Field label="Sito web"><Input value={f.website} onChange={v => setF({...f, website: v})} placeholder="https://..." testid="field-website"/></Field>
        <Field label="Email"><Input type="email" value={f.email} onChange={v => setF({...f, email: v})} testid="field-email"/></Field>
        <Field label="Indirizzo" wide><Input value={f.address} onChange={v => setF({...f, address: v})} testid="field-address"/></Field>
        <Field label="Descrizione" wide>
          <textarea
            value={f.description}
            onChange={e => setF({...f, description: e.target.value})}
            rows={4}
            data-testid="field-description"
            className="w-full bg-white/5 border border-white/15 rounded-lg px-3 py-2.5 text-white text-base focus:outline-none focus:border-[color:var(--yup-primary)]"
            placeholder="Racconta cosa rende speciale la tua attività..."
          />
        </Field>
      </div>

      {/* Hours per day */}
      <SectionTitle icon={Store}>Orari settimanali</SectionTitle>
      <HoursEditor hours={f.hours} onChange={h => setF({...f, hours: h})} />

      {/* Extraordinary closures */}
      <SectionTitle icon={CalendarX}>Chiusure straordinarie</SectionTitle>
      <div className="flex flex-wrap gap-2 mb-2" data-testid="closures-list">
        {(f.extraordinary_closures || []).map(d => (
          <span key={d} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-red-500/15 text-red-300 border border-red-500/25 text-xs">
            {d}
            <button onClick={() => setF({...f, extraordinary_closures: f.extraordinary_closures.filter(x => x !== d)})} className="hover:text-white">
              <X size={12}/>
            </button>
          </span>
        ))}
      </div>
      <div className="flex gap-2 items-center">
        <input
          type="date"
          value={closureDate}
          onChange={e => setClosureDate(e.target.value)}
          data-testid="closure-date-input"
          className="bg-white/5 border border-white/15 rounded-lg px-3 py-2 text-white text-sm"
        />
        <button
          onClick={() => {
            if (closureDate && !f.extraordinary_closures.includes(closureDate)) {
              setF({...f, extraordinary_closures: [...f.extraordinary_closures, closureDate].sort()});
              setClosureDate('');
            }
          }}
          data-testid="closure-add-btn"
          className="px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-sm text-white/80"
        >+ Aggiungi</button>
      </div>

      {/* Amenity tags */}
      <SectionTitle icon={Tag}>Caratteristiche</SectionTitle>
      <div className="flex flex-wrap gap-2" data-testid="tags-selector">
        {AVAILABLE_TAGS.map(t => {
          const active = f.tags.includes(t.key);
          return (
            <button
              key={t.key}
              onClick={() => setF({...f, tags: active ? f.tags.filter(x => x !== t.key) : [...f.tags, t.key]})}
              data-testid={`tag-${t.key}`}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition ${
                active
                  ? 'bg-[color:var(--yup-primary)]/15 border-[color:var(--yup-primary)] text-[color:var(--yup-primary)]'
                  : 'border-white/15 text-white/60 hover:border-white/30 hover:text-white'
              }`}
            >
              {active && '✓ '}{t.label}
            </button>
          );
        })}
      </div>

      {/* Social */}
      <SectionTitle icon={Instagram}>Social & Menu</SectionTitle>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <Field label={<span className="inline-flex items-center gap-1.5"><Instagram size={13}/> Instagram</span>}>
          <Input value={f.instagram_url} onChange={v => setF({...f, instagram_url: v})} placeholder="https://instagram.com/..." testid="field-instagram"/>
        </Field>
        <Field label={<span className="inline-flex items-center gap-1.5"><Facebook size={13}/> Facebook</span>}>
          <Input value={f.facebook_url} onChange={v => setF({...f, facebook_url: v})} placeholder="https://facebook.com/..." testid="field-facebook"/>
        </Field>
        <Field label={<span className="inline-flex items-center gap-1.5"><FileText size={13}/> Menu / Listino (PDF o immagine)</span>} wide>
          <MenuUpload url={f.menu_url} onChange={v => setF({...f, menu_url: v})} />
        </Field>
      </div>

      {/* Featured offer text */}
      <SectionTitle icon={Sparkles}>Offerta in evidenza</SectionTitle>
      <p className="text-xs text-white/50 mb-2">Breve testo mostrato in evidenza sulla card. Per offerte a tempo con badge/scadenza usa il tab "Offerte flash".</p>
      <Input
        value={f.featured_offer_text}
        onChange={v => setF({...f, featured_offer_text: v})}
        placeholder="Es. Aperitivo lago 2x1 dalle 18:00"
        testid="field-featured-offer"
      />

      <div className="mt-6 flex justify-end">
        <button
          onClick={handleSave}
          disabled={saving}
          data-testid={`sponsor-save-${activity.id}`}
          className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-bold text-black text-sm uppercase tracking-wider disabled:opacity-60"
          style={{ background: 'linear-gradient(135deg, #f2e5b8, #BCAC8A)' }}
        >
          {saving ? <Loader2 size={14} className="animate-spin"/> : <Save size={14}/>} Salva modifiche
        </button>
      </div>
    </div>
  );
}

// ============ GALLERY EDITOR (drag&drop, reorder, cover pick) ============

function GalleryEditor({ image, gallery, onChange }) {
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [progress, setProgress] = useState(0);
  const [dragIndex, setDragIndex] = useState(null);
  const inputRef = useRef(null);

  const allPhotos = [image, ...(gallery || []).filter(g => g !== image)].filter(Boolean);

  const doUpload = async (files) => {
    setUploading(true); setProgress(0);
    const newUrls = [];
    for (const f of files) {
      try {
        const r = await sponsorApi.upload(f, p => setProgress(p));
        newUrls.push(r.url);
      } catch (e) {
        alert('Upload fallito per ' + f.name + ': ' + (e.response?.data?.detail || e.message));
      }
    }
    // If we had no image before, first uploaded becomes cover
    if (!image && newUrls.length > 0) {
      onChange({ image: newUrls[0], gallery: [...(gallery || []), ...newUrls.slice(1)] });
    } else {
      onChange({ image, gallery: [...(gallery || []), ...newUrls] });
    }
    setUploading(false); setProgress(0);
  };

  const onFileInput = e => {
    const files = Array.from(e.target.files || []).filter(f => f.type.startsWith('image/'));
    if (files.length) doUpload(files);
    e.target.value = '';
  };

  const onDrop = e => {
    e.preventDefault(); setDragOver(false);
    const files = Array.from(e.dataTransfer.files || []).filter(f => f.type.startsWith('image/'));
    if (files.length) doUpload(files);
  };

  const setCover = (url) => {
    const nextGallery = allPhotos.filter(p => p !== url);
    onChange({ image: url, gallery: nextGallery });
  };

  const removePhoto = (url) => {
    if (!window.confirm('Rimuovere questa foto?')) return;
    if (url === image) {
      const [next, ...rest] = allPhotos.filter(p => p !== url);
      onChange({ image: next || '', gallery: rest });
    } else {
      onChange({ image, gallery: (gallery || []).filter(g => g !== url) });
    }
  };

  const reorder = (from, to) => {
    if (from === to) return;
    const arr = [...allPhotos];
    const [moved] = arr.splice(from, 1);
    arr.splice(to, 0, moved);
    // Cover = first in array
    onChange({ image: arr[0] || '', gallery: arr.slice(1) });
  };

  return (
    <div>
      <div
        onDragOver={e => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        onClick={() => inputRef.current?.click()}
        data-testid="gallery-drop-zone"
        className={`cursor-pointer border-2 border-dashed rounded-xl p-6 text-center transition mb-4 ${
          dragOver
            ? 'border-[color:var(--yup-primary)] bg-[color:var(--yup-primary)]/5'
            : 'border-white/15 hover:border-white/30 bg-white/[0.02]'
        }`}
      >
        <input ref={inputRef} type="file" accept="image/*" multiple hidden onChange={onFileInput} data-testid="gallery-file-input"/>
        <Upload size={28} className="mx-auto text-white/50 mb-2"/>
        <div className="text-sm text-white/80 font-medium">
          {uploading ? 'Caricamento...' : 'Trascina foto qui o clicca per selezionarle'}
        </div>
        <div className="text-xs text-white/50 mt-1">JPG / PNG / WEBP · max 8 MB · più foto insieme</div>
        {uploading && (
          <div className="mt-3 max-w-xs mx-auto bg-black/40 rounded-full h-2 overflow-hidden">
            <div className="h-full bg-[color:var(--yup-primary)] transition-all" style={{ width: `${progress}%` }} data-testid="gallery-upload-progress"/>
          </div>
        )}
      </div>

      {allPhotos.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3" data-testid="gallery-grid">
          {allPhotos.map((url, i) => {
            const isCover = i === 0;
            return (
              <div
                key={url}
                draggable
                onDragStart={() => setDragIndex(i)}
                onDragOver={e => e.preventDefault()}
                onDrop={() => { reorder(dragIndex, i); setDragIndex(null); }}
                data-testid={`gallery-photo-${i}`}
                className={`group relative aspect-[4/3] rounded-lg overflow-hidden border-2 ${isCover ? 'border-[color:var(--yup-primary)]' : 'border-white/10'}`}
              >
                <img src={resolveImage(url)} alt="" className="w-full h-full object-cover"/>
                {isCover && (
                  <span className="absolute top-1.5 left-1.5 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] font-bold uppercase tracking-widest bg-[color:var(--yup-primary)] text-black">
                    <Star size={9} fill="currentColor"/> Copertina
                  </span>
                )}
                <div className="absolute inset-x-0 bottom-0 p-1.5 flex justify-between items-center bg-gradient-to-t from-black/85 to-transparent opacity-0 group-hover:opacity-100 transition">
                  <span className="text-white/70 cursor-grab"><GripVertical size={14}/></span>
                  <div className="flex gap-1">
                    {!isCover && (
                      <button
                        onClick={e => { e.stopPropagation(); setCover(url); }}
                        title="Imposta come copertina"
                        data-testid={`gallery-set-cover-${i}`}
                        className="p-1 rounded bg-white/15 hover:bg-white/25"
                      ><Star size={12}/></button>
                    )}
                    <button
                      onClick={e => { e.stopPropagation(); removePhoto(url); }}
                      title="Rimuovi"
                      data-testid={`gallery-remove-${i}`}
                      className="p-1 rounded bg-red-500/40 hover:bg-red-500/60"
                    ><X size={12}/></button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ============ HOURS EDITOR (7 giorni) ============
const DAYS = [
  { k: 'mon', l: 'Lun' }, { k: 'tue', l: 'Mar' }, { k: 'wed', l: 'Mer' },
  { k: 'thu', l: 'Gio' }, { k: 'fri', l: 'Ven' }, { k: 'sat', l: 'Sab' }, { k: 'sun', l: 'Dom' }
];

function HoursEditor({ hours, onChange }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid="hours-editor">
      {DAYS.map(d => (
        <div key={d.k} className="flex items-center gap-2">
          <span className="text-xs uppercase tracking-widest text-white/50 w-10 font-semibold">{d.l}</span>
          <input
            value={hours[d.k] || ''}
            onChange={e => onChange({ ...hours, [d.k]: e.target.value })}
            placeholder="Es. 09:00-12:30, 15:00-19:00 o 'chiuso'"
            data-testid={`hours-${d.k}`}
            className="flex-1 bg-white/5 border border-white/15 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[color:var(--yup-primary)]"
          />
        </div>
      ))}
    </div>
  );
}

// ============ MENU UPLOAD ============
function MenuUpload({ url, onChange }) {
  const [uploading, setUploading] = useState(false);
  const inputRef = useRef(null);

  const doUpload = async (file) => {
    setUploading(true);
    try {
      const r = await sponsorApi.upload(file);
      onChange(r.url);
    } catch (e) { alert('Upload fallito: ' + (e.response?.data?.detail || e.message)); }
    finally { setUploading(false); }
  };

  return (
    <div className="flex items-center gap-2">
      <input
        ref={inputRef} type="file" accept="image/*,application/pdf" hidden
        data-testid="menu-file-input"
        onChange={e => { const f = e.target.files?.[0]; if (f) doUpload(f); e.target.value = ''; }}
      />
      {url ? (
        <>
          <a href={resolveImage(url)} target="_blank" rel="noopener noreferrer"
             className="inline-flex items-center gap-1.5 text-[color:var(--yup-primary)] text-sm hover:underline"
             data-testid="menu-view-link">
            <FileText size={14}/> Visualizza menu <ExternalLink size={10}/>
          </a>
          <button
            onClick={() => onChange('')}
            data-testid="menu-remove-btn"
            className="text-xs text-red-400 hover:text-red-300 ml-2"
          >Rimuovi</button>
        </>
      ) : (
        <button
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
          data-testid="menu-upload-btn"
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 text-sm text-white/80 border border-white/15"
        >
          {uploading ? <Loader2 size={14} className="animate-spin"/> : <Upload size={14}/>}
          Carica PDF o immagine
        </button>
      )}
    </div>
  );
}

// ============ UPGRADES SECTION ============

function UpgradesSection({ activity, upgrades, onChange, notify }) {
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState(null); // target_level

  const currentLevel = activity.sponsor_level || 'none';
  const currentIdx = TIER_HIERARCHY.indexOf(currentLevel);
  const higherTiers = TIER_HIERARCHY.slice(currentIdx + 1);
  const pendingUpgrade = upgrades.find(u => ['pending', 'approved'].includes(u.status));

  const request = async (target) => {
    setBusy(true);
    try {
      await sponsorApi.createUpgrade({ activity_id: activity.id, target_level: target, duration_months: 1 });
      await onChange();
      setSelected(null);
      notify('Richiesta upgrade inviata! Lo staff YUP la esaminerà.', 'ok');
    } catch (e) { notify('Errore: ' + (e.response?.data?.detail || e.message), 'err'); }
    finally { setBusy(false); }
  };

  const cancel = async (uid) => {
    if (!window.confirm('Annullare la richiesta?')) return;
    try { await sponsorApi.cancelUpgrade(uid); await onChange(); notify('Richiesta annullata', 'ok'); }
    catch (e) { notify('Errore: ' + (e.response?.data?.detail || e.message), 'err'); }
  };

  return (
    <div className="space-y-6">
      {/* Current level card */}
      <div className="rounded-2xl border border-[color:var(--yup-primary)]/30 bg-gradient-to-br from-black to-[color:var(--yup-primary)]/10 p-5" data-testid="upgrades-current-level">
        <div className="text-xs uppercase tracking-widest text-white/50 font-semibold mb-1">Livello attuale</div>
        <div className="flex items-center gap-3">
          <Crown size={24} className={TIER_INFO[currentLevel].color}/>
          <div>
            <div className={`text-2xl font-bold ${TIER_INFO[currentLevel].color}`}>{TIER_INFO[currentLevel].label}</div>
            <div className="text-sm text-white/60">{TIER_INFO[currentLevel].desc}</div>
          </div>
        </div>
        {activity.sponsor_end && (
          <div className="mt-3 text-xs text-white/60">
            Scadenza: {new Date(activity.sponsor_end).toLocaleDateString('it-IT')}
          </div>
        )}
      </div>

      {pendingUpgrade && (
        <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-4 text-sm" data-testid="upgrades-pending">
          <div className="font-semibold text-amber-300 flex items-center gap-2">
            <AlertCircle size={16}/> Richiesta in attesa: {TIER_INFO[pendingUpgrade.target_level].label} — €{pendingUpgrade.price_delta}
          </div>
          <div className="text-amber-200/70 text-xs mt-1">Stato: <strong>{pendingUpgrade.status}</strong>. Lo staff YUP ti contatterà per completare il pagamento.</div>
          <button onClick={() => cancel(pendingUpgrade.id)} data-testid="upgrade-cancel-btn"
                  className="mt-2 text-xs text-amber-200 underline hover:text-white">Annulla richiesta</button>
        </div>
      )}

      {/* Upgrade options */}
      {higherTiers.length > 0 && !pendingUpgrade && (
        <div>
          <div className="text-white text-lg font-bold mb-3">Passa a un livello superiore</div>
          <p className="text-sm text-white/60 mb-4">Ti mostriamo solo la differenza rispetto al tuo piano attuale. Il pagamento verrà gestito dallo staff YUP dopo l'invio della richiesta.</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {higherTiers.map(t => {
              const info = TIER_INFO[t];
              const delta = info.price - TIER_INFO[currentLevel].price;
              return (
                <button
                  key={t}
                  onClick={() => setSelected(t)}
                  data-testid={`upgrade-option-${t}`}
                  className={`text-left rounded-xl border p-4 transition ${
                    selected === t
                      ? 'border-[color:var(--yup-primary)] bg-[color:var(--yup-primary)]/10'
                      : 'border-white/10 bg-white/[0.03] hover:border-[color:var(--yup-primary)]/40'
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <Crown size={18} className={info.color}/>
                    <div className={`text-lg font-bold ${info.color}`}>{info.label}</div>
                  </div>
                  <div className="text-xs text-white/60 mt-1">{info.desc}</div>
                  <div className="mt-3 text-xs text-white/50 line-through">€{info.price}/mese</div>
                  <div className="text-2xl font-bold text-[color:var(--yup-primary)]">+€{delta.toFixed(0)}</div>
                  <div className="text-[10px] uppercase tracking-widest text-white/40">Differenza rispetto al tuo piano</div>
                </button>
              );
            })}
          </div>
          {selected && (
            <div className="mt-4 flex justify-end">
              <button
                onClick={() => request(selected)}
                disabled={busy}
                data-testid="upgrade-request-btn"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-bold text-black text-sm uppercase tracking-wider disabled:opacity-60"
                style={{ background: 'linear-gradient(135deg, #f2e5b8, #BCAC8A)' }}
              >
                {busy ? <Loader2 size={14} className="animate-spin"/> : <CheckCircle2 size={14}/>}
                Richiedi upgrade a {TIER_INFO[selected].label}
              </button>
            </div>
          )}
        </div>
      )}

      {higherTiers.length === 0 && (
        <div className="text-center py-8 text-white/60">
          <Crown size={40} className="mx-auto mb-3 text-[color:var(--yup-primary)]/50"/>
          Hai già il livello massimo di sponsorizzazione. Grazie!
        </div>
      )}

      {/* Storico */}
      {upgrades.length > 0 && (
        <div>
          <div className="text-white text-sm font-bold mb-2 mt-6">Storico richieste</div>
          <div className="rounded-xl border border-white/10 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-white/5 text-white/70">
                <tr>
                  <th className="px-3 py-2 text-left">Data</th>
                  <th className="px-3 py-2 text-left">Da → A</th>
                  <th className="px-3 py-2 text-right">Prezzo</th>
                  <th className="px-3 py-2 text-left">Stato</th>
                </tr>
              </thead>
              <tbody>
                {upgrades.map(u => (
                  <tr key={u.id} className="border-t border-white/5" data-testid={`upgrade-row-${u.id}`}>
                    <td className="px-3 py-2 text-white/70 text-xs">{new Date(u.created_at).toLocaleDateString('it-IT')}</td>
                    <td className="px-3 py-2 text-white">
                      <span className={TIER_INFO[u.current_level].color}>{TIER_INFO[u.current_level].label}</span> → <span className={TIER_INFO[u.target_level].color}>{TIER_INFO[u.target_level].label}</span>
                    </td>
                    <td className="px-3 py-2 text-right text-[color:var(--yup-primary)] font-bold">€{u.price_delta}</td>
                    <td className="px-3 py-2">
                      <span className={`text-xs uppercase font-bold px-2 py-0.5 rounded-full ${
                        u.status === 'paid' || u.status === 'approved' ? 'bg-emerald-500/20 text-emerald-300' :
                        u.status === 'pending' ? 'bg-amber-500/20 text-amber-300' :
                        'bg-red-500/20 text-red-300'
                      }`}>{u.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ============ OFFERS SECTION (invariato dalla versione precedente) ============

function OffersSection({ activities, offers, onChange }) {
  const [editing, setEditing] = useState(null);
  const [uploadingImg, setUploadingImg] = useState(false);
  const [saveError, setSaveError] = useState('');
  const EMPTY = { title: '', description: '', promo_text: '', activity_id: activities[0]?.id || '', is_active: true, image: '' };

  const handleImageUpload = async (file) => {
    if (!file || !file.type.startsWith('image/')) { alert('Seleziona un\'immagine JPG, PNG o WEBP'); return; }
    setUploadingImg(true);
    try {
      const r = await sponsorApi.upload(file);
      setEditing(e => ({ ...e, image: r.url }));
    } catch (e) { alert('Upload fallito: ' + (e.response?.data?.detail || e.message)); }
    finally { setUploadingImg(false); }
  };

  const onDropFile = (e) => {
    e.preventDefault(); e.stopPropagation();
    const f = e.dataTransfer.files?.[0];
    if (f) handleImageUpload(f);
  };

  const save = async () => {
    setSaveError('');
    if (!editing.title || !editing.activity_id) { setSaveError('Titolo e attività obbligatori'); return; }
    const now = new Date();
    const expires = new Date(now.getTime() + 7 * 24 * 3600e3);
    const payload = {
      ...editing,
      starts_at: editing.starts_at || now.toISOString(),
      expires_at: editing.expires_at || expires.toISOString(),
      city_slug: activities.find(a => a.id === editing.activity_id)?.city_slug,
    };
    try {
      if (editing.id) await sponsorApi.updateOffer(editing.id, payload);
      else await sponsorApi.createOffer(payload);
      setEditing(null); onChange();
    } catch (e) {
      // Show moderation error clearly
      setSaveError(e.response?.data?.detail || e.message || 'Errore salvataggio');
    }
  };
  const del = async (o) => { if (!window.confirm('Eliminare offerta?')) return; await sponsorApi.deleteOffer(o.id); onChange(); };

  return (
    <div>
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-white text-lg font-bold">Le mie offerte flash ({offers.length})</h2>
        <button onClick={() => { setEditing({...EMPTY}); setSaveError(''); }} data-testid="sponsor-new-offer-btn"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full font-bold text-black text-sm"
                style={{ background: 'linear-gradient(135deg, #f2e5b8, #BCAC8A)' }}>+ Nuova offerta</button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {offers.map(o => (
          <div key={o.id} className="rounded-xl bg-white/[0.03] border border-white/10 p-4 flex gap-3">
            {o.image && (
              <img src={resolveImage(o.image)} alt="" className="w-24 h-24 rounded-lg object-cover flex-shrink-0" />
            )}
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="text-lg font-bold text-white">{o.title}</div>
                  <div className="text-[color:var(--yup-primary)] font-bold text-sm">{o.promo_text}</div>
                </div>
                <div className="flex gap-1 flex-shrink-0">
                  <button onClick={() => { setEditing({...o}); setSaveError(''); }} className="text-white/70 hover:text-white text-xs">Modifica</button>
                  <button onClick={() => del(o)} className="text-red-400 hover:text-red-300 text-xs ml-2">Elimina</button>
                </div>
              </div>
              <p className="text-white/60 text-sm mt-2 line-clamp-3">{o.description}</p>
              <div className="text-xs text-white/40 mt-2 flex items-center gap-2 flex-wrap">
                <span>Scade: {o.expires_at ? new Date(o.expires_at).toLocaleString('it-IT') : '—'}</span>
                {o.title_i18n && Object.keys(o.title_i18n).length > 1 && (
                  <span className="text-emerald-400" title={Object.keys(o.title_i18n).join(', ')}>✓ tradotto in {Object.keys(o.title_i18n).filter(k => k !== 'it').length} lingue</span>
                )}
              </div>
            </div>
          </div>
        ))}
        {offers.length === 0 && <div className="col-span-full text-center py-12 text-white/50">Nessuna offerta ancora. Creane una!</div>}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/70 backdrop-blur-sm sm:p-4" onClick={() => setEditing(null)}>
          <div className="w-full max-w-lg bg-[#0f0f0f] rounded-t-2xl sm:rounded-xl border border-[color:var(--yup-primary)]/40 p-6 max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <h3 className="text-white font-bold text-lg mb-4">{editing.id ? 'Modifica offerta' : 'Nuova offerta'}</h3>
            <div className="space-y-3">
              <Field label="Attività">
                <select value={editing.activity_id} onChange={e => setEditing({...editing, activity_id: e.target.value})}
                        data-testid="offer-activity-select"
                        className="w-full bg-white/5 border border-white/15 rounded-lg px-3 py-2.5 text-white">
                  {activities.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
              </Field>

              {/* Photo upload with drag&drop + preview */}
              <Field label="Foto (opzionale)">
                {editing.image ? (
                  <div className="relative inline-block" data-testid="offer-image-preview-wrap">
                    <img src={resolveImage(editing.image)} alt="" className="w-full max-h-48 rounded-lg object-cover" />
                    <button
                      onClick={() => setEditing({...editing, image: ''})}
                      data-testid="offer-image-remove"
                      className="absolute top-2 right-2 w-8 h-8 rounded-full bg-red-500/85 hover:bg-red-500 text-white flex items-center justify-center"
                      title="Rimuovi immagine"
                    >
                      <X size={14}/>
                    </button>
                    <label className="absolute bottom-2 right-2 px-2.5 py-1 rounded-full bg-black/70 hover:bg-black/85 text-white text-xs font-semibold cursor-pointer border border-white/25" data-testid="offer-image-replace">
                      Sostituisci
                      <input type="file" accept="image/jpeg,image/png,image/webp" hidden
                             onChange={e => { const f = e.target.files?.[0]; if (f) handleImageUpload(f); e.target.value = ''; }} />
                    </label>
                  </div>
                ) : (
                  <label
                    onDragOver={e => { e.preventDefault(); e.stopPropagation(); }}
                    onDrop={onDropFile}
                    data-testid="offer-image-dropzone"
                    className="cursor-pointer flex flex-col items-center justify-center border-2 border-dashed border-white/15 hover:border-[color:var(--yup-primary)]/40 rounded-lg p-6 text-center bg-white/[0.02] transition"
                  >
                    <input type="file" accept="image/jpeg,image/png,image/webp,image/*" capture="environment" hidden
                           onChange={e => { const f = e.target.files?.[0]; if (f) handleImageUpload(f); e.target.value = ''; }} />
                    {uploadingImg ? (
                      <><Loader2 size={22} className="animate-spin text-white/60 mb-2"/> <span className="text-sm text-white/60">Caricamento...</span></>
                    ) : (
                      <>
                        <Upload size={22} className="text-white/40 mb-2"/>
                        <span className="text-sm text-white/70 font-medium">Trascina o tocca per caricare</span>
                        <span className="text-xs text-white/40 mt-1">JPG / PNG / WEBP · max 8MB</span>
                      </>
                    )}
                  </label>
                )}
              </Field>

              <Field label="Titolo (italiano)"><Input value={editing.title} onChange={v => setEditing({...editing, title: v})} testid="offer-title" /></Field>
              <Field label="Badge (es. -30%, 2x1)"><Input value={editing.promo_text} onChange={v => setEditing({...editing, promo_text: v})} testid="offer-badge" /></Field>
              <Field label="Descrizione (italiano)">
                <textarea value={editing.description || ''} onChange={e => setEditing({...editing, description: e.target.value})}
                          rows={3} data-testid="offer-description"
                          className="w-full bg-white/5 border border-white/15 rounded-lg px-3 py-2.5 text-white text-base" />
                <div className="text-[10px] text-white/40 mt-1 inline-flex items-center gap-1">
                  <Sparkles size={10}/> Il titolo e la descrizione verranno tradotti automaticamente in EN e DE
                </div>
              </Field>
            </div>
            {saveError && (
              <div className="mt-3 p-3 rounded-lg bg-red-500/15 border border-red-500/40 text-red-300 text-sm inline-flex items-start gap-2" data-testid="offer-save-error">
                <AlertCircle size={14} className="flex-shrink-0 mt-0.5"/>
                <span>{saveError}</span>
              </div>
            )}
            <div className="mt-5 flex justify-end gap-2">
              <button onClick={() => setEditing(null)} className="px-4 py-2 rounded-lg bg-white/5 text-white/70 text-sm">Annulla</button>
              <button onClick={save} data-testid="sponsor-save-offer-btn" className="px-6 py-2 rounded-full font-bold text-black text-sm" style={{ background: 'linear-gradient(135deg, #f2e5b8, #BCAC8A)' }}>Salva</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============ STATS SECTION (invariato) ============
function StatsSection({ stats }) {
  if (!stats) return <div className="text-white/60 text-center py-12"><Loader2 className="inline animate-spin" size={20}/></div>;
  const totals = stats.totals || {};
  const KPI = [
    { key: 'view', label: 'Visualizzazioni' },
    { key: 'click_details', label: 'Aperture scheda' },
    { key: 'call', label: 'Chiamate' },
    { key: 'directions', label: 'Indicazioni' },
    { key: 'website', label: 'Sito web' },
  ];
  return (
    <div>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6">
        {KPI.map(k => (
          <div key={k.key} className="rounded-xl bg-white/[0.03] border border-white/10 p-4 text-center">
            <div className="text-3xl font-bold text-[color:var(--yup-primary)]">{totals[k.key] || 0}</div>
            <div className="text-xs uppercase tracking-widest text-white/60 mt-1">{k.label}</div>
          </div>
        ))}
      </div>
      <div className="text-xs text-white/50 mb-3">Ultimi {stats.days || 30} giorni</div>
      <div className="rounded-xl bg-white/[0.03] border border-white/10 overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-white/5">
            <tr>
              <th className="px-3 py-2 text-left text-white/70 font-semibold">Attività</th>
              <th className="px-3 py-2 text-right text-white/70 font-semibold">Views</th>
              <th className="px-3 py-2 text-right text-white/70 font-semibold">Aperture</th>
              <th className="px-3 py-2 text-right text-white/70 font-semibold hidden sm:table-cell">Call</th>
              <th className="px-3 py-2 text-right text-white/70 font-semibold hidden sm:table-cell">Sito</th>
            </tr>
          </thead>
          <tbody>
            {(stats.rows || []).map(r => (
              <tr key={r.activity_id} className="border-t border-white/5">
                <td className="px-3 py-2 text-white">{r.name}</td>
                <td className="px-3 py-2 text-right text-white/80">{r.view || 0}</td>
                <td className="px-3 py-2 text-right text-white/80">{r.click_details || 0}</td>
                <td className="px-3 py-2 text-right text-white/80 hidden sm:table-cell">{r.call || 0}</td>
                <td className="px-3 py-2 text-right text-white/80 hidden sm:table-cell">{r.website || 0}</td>
              </tr>
            ))}
            {(stats.rows || []).length === 0 && (
              <tr><td colSpan={5} className="px-3 py-6 text-center text-white/50">Nessun evento registrato in questo periodo</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ============ SHARED UI ============
function SectionTitle({ icon: Icon, children }) {
  return (
    <div className="mt-6 mb-3 flex items-center gap-2 text-[color:var(--yup-primary)] text-xs uppercase tracking-widest font-bold pb-2 border-b border-white/5">
      <Icon size={13}/> {children}
    </div>
  );
}
function Field({ label, children, wide }) {
  return (
    <div className={wide ? 'sm:col-span-2' : ''}>
      <div className="text-xs uppercase tracking-widest text-white/60 font-semibold mb-1.5">{label}</div>
      {children}
    </div>
  );
}
function Input({ value, onChange, type = 'text', placeholder, testid }) {
  return <input type={type} value={value || ''} onChange={e => onChange(e.target.value)} placeholder={placeholder}
                data-testid={testid}
                className="w-full bg-white/5 border border-white/15 rounded-lg px-3 py-2.5 text-white text-base focus:outline-none focus:border-[color:var(--yup-primary)]" />;
}
